import { z } from "zod";

function useIsStringSchema() {
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;
  return z.string({
    required_error: t("common.invalid"),
    invalid_type_error: t("common.invalid"),
  });
}

export function useErrorVars(vars: Record<string, unknown>) {
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;
  return {
    required_error: t("form.invalid_field", vars),
    invalid_type_error: "this is not a string",
  };
}

export function useDateErrorVars(vars: Record<string, unknown>) {
  // https://github.com/colinhacks/zod/issues/1526 and https://github.com/colinhacks/zod/discussions/1851 for reference
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;
  return {
    errorMap: (issue, { defaultError }) => ({message: issue.code === "invalid_date" ? t("form.invalid_field", vars) : defaultError})  
  }
}

export function useVATNameSchema() {
  return z.object({
    vat_name: useIsStringSchema().nullish(),
  });
}

export function useVATNumberSchema() {
  return z.object({
    vat_number: useIsStringSchema().nullish(),
  });
}

export function useVATAddressSchema() {
  return z.object({
    address: useIsStringSchema().nullish(),
  });
}

export function useVATZipCodeSchema() {
  return z.object({
    zip_code: useIsStringSchema().nullish(),
  });
}

export function useVATCitySchema() {
  return z.object({
    city: useIsStringSchema().nullish(),
  });
}

export function useVATCountrySchema() {
  return z.object({
    country: useIsStringSchema().nullish(),
  });
}

export function useNameSchema() {
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;

  return z.object({
    name: z
      .string(useErrorVars({ name: t("form.register.name").toLowerCase() }))
      .min(2, { message: t("form.register.name.too_short") })
      .max(30, { message: t("form.register.name.too_long") }),
  });
}

export function useEmailSchema() {
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;

  return z.object({
    email: z
      .string(useErrorVars({ name: t("form.register.email").toLowerCase() }))
      .email(),
  });
}

export function useBirthdaySchema() {
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;

  const minAge = new Date(
    new Date().setFullYear(new Date().getFullYear() - 100)
  );
  const maxAge = new Date(
    new Date().setFullYear(new Date().getFullYear() - 14)
  );

  return z.object({
    birthday: z.coerce
      .date(useDateErrorVars({ name: t("form.register.dob").toLowerCase() }))
      .min(minAge, { message: t("form.register.dob.too_old") })
      .max(maxAge, { message: t("form.register.dob.too_young") }),
  });
}

export function useCheckboxSchema() {
  return z.object({
    checkbox: z.boolean(),
  });
}

export function usePhoneSchema() {
  return z.object({
    phone: z.string(),
  });
}

export function useSchemaMerger(schemas: z.ZodObject<any>[]) {
  return schemas.reduce((acc, curr) => acc.merge(curr), z.object({}));
}

export async function useValidateFormState(schema: Zod.AnyZodObject, state: any) {
  const result = await schema.safeParseAsync(state);
  if (result.success === false) {
    return result.error.issues.map((issue) => ({
      path: issue.path.join("."),
      message: issue.message,
    }));
  }
  return [];
}
